import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';
import { GxJourneyTab } from '../components/gx-console/GxJourneyTab';
import { GxRolesManagement } from '../components/gx-console/GxRolesManagement';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  TEXT_SECONDARY,
  BORDER_LIGHT,
  BG_PAGE,
  BG_CARD,
  BG_SURFACE,
  FONT_HEADING,
} from '../components/legacy-ball/table-management/designTokens';

/* ─── Types ─── */

type SubView = 'dashboard' | 'journey' | 'guest-lookup' | 'venues' | 'faq' | 'task-roles';

const SUB_VIEWS: { key: SubView; label: string; icon: string }[] = [
  { key: 'dashboard', label: 'Dashboard', icon: '📊' },
  { key: 'journey', label: 'Journey', icon: '🗺️' },
  { key: 'guest-lookup', label: 'Guest Lookup', icon: '🔍' },
  { key: 'venues', label: 'Venues', icon: '📍' },
  { key: 'faq', label: 'FAQ Reference', icon: '❓' },
  { key: 'task-roles', label: 'Task Roles', icon: '🏷️' },
];

interface EventData {
  id: string;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  venue?: {
    name: string;
    address?: string;
    parkingInfo?: string;
    capacity?: number;
  };
  transportInfo?: string;
}

interface GuestSeat {
  id: string;
  seatNumber: number;
  status: string;
  checkedIn?: boolean;
  checkedInAt?: string;
  table: {
    id: string;
    tableNumber: number;
    tableName?: string;
    zone?: { id: string; name: string };
  };
}

interface GuestResult {
  id: string;
  fullName: string;
  ticketNumber?: string;
  chapterAffiliation?: string;
  familyAssociation?: string;
  dietaryPreference?: string;
  accessibilityNeeds?: string;
  rsvpStatus?: string;
  paymentStatus?: string;
  sponsorshipLevel?: string;
  protocolVip?: boolean;
  seat?: GuestSeat[];
  registration?: {
    id: string;
    ticket?: {
      tierName?: string;
      ticketNumber?: number;
      orderId?: string;
      order?: { referenceId?: string };
    };
  };
}

/* ─── Utilities ─── */

function formatTime(timeStr: string): string {
  if (!timeStr) return '';
  let hours: number, minutes: number;
  if (timeStr.includes('T')) {
    const timePart = timeStr.split('T')[1].slice(0, 5);
    [hours, minutes] = timePart.split(':').map(Number);
  } else if (timeStr.includes(':')) {
    [hours, minutes] = timeStr.split(':').map(Number);
  } else {
    return timeStr;
  }
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function getTodayDateString(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getGoogleMapsUrl(address: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

/* ─── Styles ─── */

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '1.5rem',
    maxWidth: '1000px',
    margin: '0 auto',
    backgroundColor: BG_PAGE,
    minHeight: '100vh',
  },
  header: {
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: PRIMARY,
    margin: '0 0 0.25rem 0',
    fontFamily: FONT_HEADING,
  },
  subtitle: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    margin: 0,
  },
  subNav: {
    display: 'flex',
    gap: '0.25rem',
    borderBottom: `2px solid ${BORDER_LIGHT}`,
    marginBottom: '1.25rem',
    overflowX: 'auto' as const,
  },
  subTab: {
    padding: '0.6rem 0.9rem',
    border: 'none',
    background: 'none',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: TEXT_MUTED,
    cursor: 'pointer',
    borderBottom: '2px solid transparent',
    marginBottom: '-2px',
    whiteSpace: 'nowrap' as const,
  },
  subTabActive: {
    padding: '0.6rem 0.9rem',
    border: 'none',
    background: 'none',
    fontSize: '0.85rem',
    fontWeight: 700,
    color: PRIMARY,
    cursor: 'pointer',
    borderBottom: `2px solid ${PRIMARY}`,
    marginBottom: '-2px',
    whiteSpace: 'nowrap' as const,
  },
  card: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '12px',
    padding: '1.25rem',
    marginBottom: '1rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  cardTitle: {
    fontSize: '1rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: '0 0 0.75rem 0',
  },
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: '0.75rem',
    marginBottom: '1rem',
  },
  statCard: {
    backgroundColor: BG_SURFACE,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '1rem',
    textAlign: 'center' as const,
  },
  statNumber: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: PRIMARY,
    margin: '0 0 0.2rem 0',
  },
  statLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.3px',
  },
  searchInput: {
    width: '100%',
    padding: '0.75rem 1rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
    marginBottom: '1rem',
  },
  guestCard: {
    backgroundColor: BG_SURFACE,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '0.75rem',
  },
  badge: {
    display: 'inline-block',
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '0.15rem 0.5rem',
    borderRadius: '10px',
  },
  directionsBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
    padding: '0.4rem 0.6rem',
    backgroundColor: '#e8f5e9',
    color: PRIMARY,
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    textDecoration: 'none',
    marginTop: '0.4rem',
  },
  faqSection: {
    marginBottom: '1.25rem',
  },
  faqSectionTitle: {
    fontSize: '0.95rem',
    fontWeight: 700,
    color: PRIMARY,
    margin: '0 0 0.5rem 0',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
  },
  faqItem: {
    backgroundColor: BG_SURFACE,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    padding: '0.75rem 1rem',
    marginBottom: '0.5rem',
  },
};

/* ─── FAQ Data ─── */

interface FaqEntry {
  question: string;
  answer: string;
}

interface FaqCategory {
  title: string;
  emoji: string;
  items: FaqEntry[];
}

const FAQ_DATA: FaqCategory[] = [
  {
    title: 'Lookup Commands',
    emoji: '🔍',
    items: [
      { question: 'How do I look up a ticket?', answer: 'Ask: "Show me the ticket for 042" or "TKT-042". Returns full guest profile including name, chapter, table/seat, dietary, and payment status.' },
      { question: 'How do I look up a table?', answer: 'Ask: "Who is at table 5" or "Table VIP". Returns all guests assigned to that table with seat numbers.' },
      { question: 'How do I search for a guest?', answer: 'Ask: "Find guest Smith" or "Look up Leonora". Partial name search returns up to 5 matches.' },
    ],
  },
  {
    title: 'Event Information',
    emoji: '📅',
    items: [
      { question: 'What time does an event start?', answer: 'Ask: "What time does the Legacy Ball start" — returns start time, end time, and venue.' },
      { question: "What's happening on a specific day?", answer: 'Ask: "Events on Saturday" or "Schedule for Friday" — returns all events for that day.' },
      { question: 'Where is an event held?', answer: 'Ask: "Where is the Legacy Ball" — returns venue name and address with directions link.' },
    ],
  },
  {
    title: 'Venue & Logistics',
    emoji: '🏨',
    items: [
      { question: "What's the dress code?", answer: 'Friday: Smart Casual. Saturday Legacy Ball: Formal/Black-Tie. Sunday: Smart Casual.' },
      { question: 'Where do I park?', answer: 'Each event card shows parking info. Ask: "Where do I park" for venue-specific details.' },
      { question: 'Is there a shuttle?', answer: 'Ask: "Shuttle schedule" or "Transportation to the ball" — returns shuttle times and pickup locations.' },
      { question: 'How do I get directions?', answer: 'Each event card has a "Get Directions" button that opens Google Maps. Or ask the chatbot.' },
    ],
  },
  {
    title: 'Personalized Queries',
    emoji: '🙋',
    items: [
      { question: "What's my meal selection?", answer: "Ask: \"What's my meal\" — returns the guest's selected meal option for the Legacy Ball." },
      { question: 'Where am I sitting?', answer: 'Ask: "My seat" or "Where am I sitting" — returns table name/number, seat number, and zone.' },
      { question: "What's next on the schedule?", answer: "Ask: \"What's next\" — returns the next upcoming event based on current time." },
      { question: 'Who from my chapter is here?', answer: 'Ask: "Who from London is here" — returns list of attendees from that chapter.' },
    ],
  },
  {
    title: 'Emergency & Help',
    emoji: '🆘',
    items: [
      { question: 'What if there is a medical emergency?', answer: 'Call 911 immediately. Each venue has a first aid station — ask any committee member.' },
      { question: 'How do I contact an organizer?', answer: 'Ask any committee member with a host badge, or use the chatbot: "Emergency contact" for organizer phone.' },
      { question: "A guest is lost or needs help?", answer: 'Direct them to the registration/welcome desk, or use the chatbot. Say "Need help" for immediate guidance.' },
    ],
  },
  {
    title: 'Pricing & General',
    emoji: '💰',
    items: [
      { question: 'How much does a ticket cost?', answer: 'Ask: "Ticket price" — returns current tier pricing information.' },
      { question: 'What is the reunion about?', answer: 'KDU Georgia 2026 Global Reunion — a multi-day celebration bringing together the global KDU community.' },
    ],
  },
];

/* ─── Main Component ─── */

export function GxConsolePage() {
  const [activeView, setActiveView] = useState<SubView>('dashboard');

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <h1 style={styles.title}>🎯 GX Team Console</h1>
        <p style={styles.subtitle}>Guest Experience committee operations dashboard</p>
      </header>

      <nav style={styles.subNav} aria-label="GX Console sections">
        {SUB_VIEWS.map((view) => (
          <button
            key={view.key}
            style={activeView === view.key ? styles.subTabActive : styles.subTab}
            onClick={() => setActiveView(view.key)}
            aria-current={activeView === view.key ? 'page' : undefined}
          >
            {view.icon} {view.label}
          </button>
        ))}
      </nav>

      {activeView === 'dashboard' && <DashboardTab />}
      {activeView === 'journey' && <JourneyTab />}
      {activeView === 'guest-lookup' && <GuestLookupTab />}
      {activeView === 'venues' && <VenuesTab />}
      {activeView === 'faq' && <FaqTab />}
    </div>
  );
}

/* ─── Journey Tab: uses imported GxJourneyTab component ─── */

function JourneyTab() {
  return <GxJourneyTab />;
}

/* ─── Dashboard Tab ─── */

function DashboardTab() {
  const [checkInCount, setCheckInCount] = useState<number>(0);
  const [totalGuests, setTotalGuests] = useState<number>(0);
  const [events, setEvents] = useState<EventData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchStats() {
      try {
        const [eventsData, statsData] = await Promise.all([
          apiClient<EventData[]>('/events'),
          apiClient<{ totalRegistered: number; checkedInCount: number }>('/legacy-ball/registrations/stats').catch(() => null),
        ]);
        setEvents(Array.isArray(eventsData) ? eventsData : []);
        if (statsData) {
          setTotalGuests(statsData.totalRegistered || 0);
          setCheckInCount(statsData.checkedInCount || 0);
        }
      } catch {
        // Silently degrade
      } finally {
        setLoading(false);
      }
    }
    fetchStats();
  }, []);

  const today = getTodayDateString();
  const todaysEvents = events.filter((e) => {
    const d = e.date?.includes('T') ? e.date.split('T')[0] : e.date;
    return d === today;
  });

  // Find next upcoming event
  const now = new Date();
  const upcoming = events
    .filter((e) => new Date(e.startTime) > now)
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  const nextEvent = upcoming[0] || null;

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem', color: TEXT_MUTED }}>Loading dashboard...</div>;
  }

  return (
    <div>
      {/* Quick Stats */}
      <div style={styles.statGrid}>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{checkInCount}</div>
          <div style={styles.statLabel}>Checked In</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{totalGuests}</div>
          <div style={styles.statLabel}>Total Guests</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{todaysEvents.length}</div>
          <div style={styles.statLabel}>Events Today</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{events.length}</div>
          <div style={styles.statLabel}>Total Events</div>
        </div>
      </div>

      {/* Next Event */}
      {nextEvent && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>⏭️ Next Event</h3>
          <div style={{ fontSize: '1.05rem', fontWeight: 700, color: TEXT_DARK }}>{nextEvent.name}</div>
          <div style={{ fontSize: '0.85rem', color: TEXT_MUTED, marginTop: '0.2rem' }}>
            {formatDate(nextEvent.date)} · {formatTime(nextEvent.startTime)} – {formatTime(nextEvent.endTime)}
          </div>
          {nextEvent.venue && (
            <div style={{ fontSize: '0.85rem', color: TEXT_SECONDARY, marginTop: '0.15rem' }}>
              📍 {nextEvent.venue.name}
            </div>
          )}
        </div>
      )}

      {/* Today's Schedule */}
      {todaysEvents.length > 0 && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>🗓️ Today&apos;s Schedule</h3>
          {todaysEvents
            .sort((a, b) => a.startTime.localeCompare(b.startTime))
            .map((evt) => (
              <div
                key={evt.id}
                style={{
                  padding: '0.6rem 0',
                  borderBottom: `1px solid ${BORDER_LIGHT}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: TEXT_DARK }}>{evt.name}</div>
                  <div style={{ fontSize: '0.78rem', color: TEXT_MUTED }}>
                    {evt.venue?.name || ''}
                  </div>
                </div>
                <div style={{ fontSize: '0.8rem', color: TEXT_SECONDARY, whiteSpace: 'nowrap' }}>
                  {formatTime(evt.startTime)}
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Quick Reports */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>📊 Quick Reports</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '0.5rem' }}>
          <a
            href="/legacy-ball"
            style={{ padding: '8px 14px', fontSize: '0.82rem', fontWeight: 600, backgroundColor: '#1a6b3c', color: '#fff', borderRadius: '6px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            🪑 Available Seats
          </a>
          <a
            href="/legacy-ball"
            style={{ padding: '8px 14px', fontSize: '0.82rem', fontWeight: 500, backgroundColor: '#f3f4f6', color: TEXT_DARK, borderRadius: '6px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px', border: `1px solid ${BORDER_LIGHT}` }}
          >
            📋 Chapter Distribution
          </a>
          <a
            href="/legacy-ball"
            style={{ padding: '8px 14px', fontSize: '0.82rem', fontWeight: 500, backgroundColor: '#f3f4f6', color: TEXT_DARK, borderRadius: '6px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px', border: `1px solid ${BORDER_LIGHT}` }}
          >
            📈 Occupancy
          </a>
        </div>
      </div>
    </div>
  );
}

/* ─── Guest Lookup Tab ─── */

function GuestLookupTab() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GuestResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!query.trim()) return;
    setSearching(true);
    setSearched(true);
    try {
      const q = query.trim();
      const response = await apiClient<{ guests: GuestResult[] }>(
        `/table-management/guests?query=${encodeURIComponent(q)}`,
      );
      setResults(response.guests || []);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [query]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <input
          type="text"
          style={{ ...styles.searchInput, marginBottom: 0, flex: 1 }}
          placeholder="Search by name, ticket #, order ID, or table..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          aria-label="Search for a guest by name or ticket number"
        />
        <button
          onClick={handleSearch}
          disabled={searching || !query.trim()}
          style={{
            padding: '0.75rem 1.25rem',
            backgroundColor: PRIMARY,
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '0.85rem',
            fontWeight: 600,
            cursor: query.trim() ? 'pointer' : 'not-allowed',
            opacity: query.trim() ? 1 : 0.6,
            whiteSpace: 'nowrap',
          }}
          aria-label="Search"
        >
          {searching ? '...' : '🔍 Search'}
        </button>
      </div>

      {searching && (
        <div style={{ textAlign: 'center', padding: '1.5rem', color: TEXT_MUTED }}>Searching...</div>
      )}

      {!searching && searched && results.length === 0 && (
        <div style={{ textAlign: 'center', padding: '1.5rem', color: TEXT_MUTED, fontSize: '0.9rem' }}>
          No guests found matching &quot;{query}&quot;
        </div>
      )}

      {results.map((guest) => {
        const seatInfo = (() => {
          if (!guest.seat || guest.seat.length === 0) return null;
          const activeSeat = guest.seat[0];
          const tName = activeSeat.table?.tableName || `Table ${activeSeat.table?.tableNumber}`;
          const zone = activeSeat.table?.zone?.name || '';
          return { table: tName, seat: `Seat ${activeSeat.seatNumber}`, zone, checkedIn: activeSeat.status === 'CheckedIn' || activeSeat.checkedIn === true };
        })();
        const isCheckedIn = seatInfo?.checkedIn || false;
        const orderRef = guest.registration?.ticket?.order?.referenceId;
        const ticketTier = guest.registration?.ticket?.tierName;

        return (
          <div key={guest.id} style={styles.guestCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.6rem' }}>
              <div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: TEXT_DARK }}>
                  {guest.fullName}
                  {guest.protocolVip && <span style={{ marginLeft: '0.4rem', fontSize: '0.75rem' }}>⭐ VIP</span>}
                </div>
                <div style={{ fontSize: '0.8rem', color: TEXT_MUTED, marginTop: '0.15rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  {guest.ticketNumber && <span>🎫 {guest.ticketNumber}</span>}
                  {ticketTier && <span>🏷️ {ticketTier}</span>}
                  {orderRef && <span>📦 {orderRef}</span>}
                </div>
              </div>
              <span style={{ ...styles.badge, backgroundColor: isCheckedIn ? '#e8f5e9' : '#f3f4f6', color: isCheckedIn ? '#2e7d32' : '#6b7280', fontSize: '0.72rem' }}>
                {isCheckedIn ? '✓ Checked In' : '○ Not Checked In'}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1rem', fontSize: '0.82rem' }}>
              {guest.chapterAffiliation && (
                <div>
                  <span style={{ fontSize: '0.68rem', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Chapter</span>
                  <div style={{ color: TEXT_DARK, fontWeight: 500 }}>{guest.chapterAffiliation}</div>
                </div>
              )}
              <div>
                <span style={{ fontSize: '0.68rem', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Table / Seat</span>
                <div style={{ color: TEXT_DARK, fontWeight: 500 }}>
                  {seatInfo ? `${seatInfo.table} · ${seatInfo.seat}` : 'Unassigned'}
                  {seatInfo?.zone && <span style={{ color: TEXT_MUTED, fontWeight: 400 }}> ({seatInfo.zone})</span>}
                </div>
              </div>
              <div>
                <span style={{ fontSize: '0.68rem', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.3px' }}>RSVP</span>
                <div style={{ color: TEXT_DARK, fontWeight: 500 }}>{guest.rsvpStatus || '—'}</div>
              </div>
              <div>
                <span style={{ fontSize: '0.68rem', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Payment</span>
                <div style={{ color: guest.paymentStatus === 'Paid' ? '#2e7d32' : guest.paymentStatus === 'Pending' ? '#d97706' : TEXT_DARK, fontWeight: 500 }}>
                  {guest.paymentStatus || '—'}
                </div>
              </div>
              <div>
                <span style={{ fontSize: '0.68rem', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Dietary</span>
                <div style={{ color: TEXT_DARK, fontWeight: 500 }}>{guest.dietaryPreference || 'None specified'}</div>
              </div>
              {guest.familyAssociation && (
                <div>
                  <span style={{ fontSize: '0.68rem', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Family</span>
                  <div style={{ color: TEXT_DARK, fontWeight: 500 }}>{guest.familyAssociation}</div>
                </div>
              )}
              {guest.sponsorshipLevel && guest.sponsorshipLevel !== 'None' && (
                <div>
                  <span style={{ fontSize: '0.68rem', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.3px' }}>Sponsor Tier</span>
                  <div style={{ color: '#6a1b9a', fontWeight: 600 }}>{guest.sponsorshipLevel}</div>
                </div>
              )}
            </div>
            {guest.accessibilityNeeds && (
              <div style={{ marginTop: '0.6rem', padding: '0.5rem 0.75rem', backgroundColor: '#fff3e0', borderRadius: '6px', border: '1px solid #ffe0b2' }}>
                <span style={{ fontSize: '0.68rem', color: '#e65100', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600 }}>♿ Accessibility Needs</span>
                <div style={{ fontSize: '0.82rem', color: '#bf360c', marginTop: '0.15rem' }}>{guest.accessibilityNeeds}</div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Venues Tab ─── */

function VenuesTab() {
  const [events, setEvents] = useState<EventData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchEvents() {
      try {
        const data = await apiClient<EventData[]>('/events');
        setEvents(Array.isArray(data) ? data : []);
      } catch {
        // Silently degrade
      } finally {
        setLoading(false);
      }
    }
    fetchEvents();
  }, []);

  // Deduplicate venues
  const venuesMap = new Map<string, EventData['venue'] & { events: string[]; transportInfo?: string }>();
  events.forEach((evt) => {
    if (evt.venue?.name) {
      if (!venuesMap.has(evt.venue.name)) {
        venuesMap.set(evt.venue.name, { ...evt.venue, events: [evt.name], transportInfo: evt.transportInfo });
      } else {
        const existing = venuesMap.get(evt.venue.name)!;
        existing.events.push(evt.name);
        if (!existing.transportInfo && evt.transportInfo) {
          existing.transportInfo = evt.transportInfo;
        }
      }
    }
  });
  const venues = Array.from(venuesMap.values());

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem', color: TEXT_MUTED }}>Loading venues...</div>;
  }

  return (
    <div>
      {venues.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem', color: TEXT_MUTED }}>No venue data available.</div>
      )}
      {venues.map((venue) => (
        <div key={venue.name} style={styles.card}>
          <div style={{ fontSize: '1rem', fontWeight: 700, color: TEXT_DARK, marginBottom: '0.4rem' }}>
            {venue.name}
          </div>
          {venue.address && (
            <div style={{ fontSize: '0.85rem', color: TEXT_SECONDARY, marginBottom: '0.3rem' }}>
              📍 {venue.address}
            </div>
          )}
          {venue.capacity && (
            <div style={{ fontSize: '0.82rem', color: TEXT_MUTED, marginBottom: '0.3rem' }}>
              👥 Capacity: {venue.capacity}
            </div>
          )}
          {venue.parkingInfo && (
            <div style={{ fontSize: '0.82rem', color: TEXT_DARK, marginBottom: '0.3rem' }}>
              🅿️ {venue.parkingInfo}
            </div>
          )}
          {venue.transportInfo && (
            <div style={{ fontSize: '0.82rem', color: TEXT_DARK, marginBottom: '0.3rem' }}>
              🚐 {venue.transportInfo}
            </div>
          )}
          <div style={{ fontSize: '0.75rem', color: TEXT_MUTED, marginBottom: '0.4rem' }}>
            Events: {venue.events?.join(', ')}
          </div>
          {venue.address && (
            <a
              href={getGoogleMapsUrl(venue.address)}
              target="_blank"
              rel="noopener noreferrer"
              style={styles.directionsBtn}
              aria-label={`Get directions to ${venue.name}`}
            >
              📍 Get Directions
            </a>
          )}
        </div>
      ))}
    </div>
  );
}

/* ─── FAQ Tab ─── */

function FaqTab() {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(FAQ_DATA.map((c) => c.title)));

  const toggleSection = (title: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  };

  const filteredData = FAQ_DATA.map((category) => ({
    ...category,
    items: category.items.filter(
      (item) =>
        !searchTerm ||
        item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchTerm.toLowerCase()),
    ),
  })).filter((category) => category.items.length > 0);

  return (
    <div>
      <input
        type="text"
        style={styles.searchInput}
        placeholder="Filter FAQ topics..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        aria-label="Filter FAQ topics"
      />

      {filteredData.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem', color: TEXT_MUTED }}>
          No FAQ items match your search.
        </div>
      )}

      {filteredData.map((category) => (
        <div key={category.title} style={styles.faqSection}>
          <button
            style={{
              ...styles.faqSectionTitle,
              background: 'none',
              border: 'none',
              padding: '0.5rem 0',
              width: '100%',
              textAlign: 'left',
            }}
            onClick={() => toggleSection(category.title)}
            aria-expanded={expandedSections.has(category.title)}
            aria-label={`${category.title} section`}
          >
            <span>{category.emoji}</span>
            <span style={{ flex: 1 }}>{category.title}</span>
            <span style={{ fontSize: '0.8rem', color: TEXT_MUTED }}>
              {expandedSections.has(category.title) ? '▼' : '▶'}
            </span>
          </button>

          {expandedSections.has(category.title) && (
            <div>
              {category.items.map((item, idx) => (
                <div key={idx} style={styles.faqItem}>
                  <div style={{ fontSize: '0.88rem', fontWeight: 600, color: TEXT_DARK, marginBottom: '0.3rem' }}>
                    {item.question}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: TEXT_SECONDARY, lineHeight: 1.5 }}>
                    {item.answer}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
// deploy trigger 1782922938
